"use client";

import { ArrowLeft, ArrowRight } from "lucide-react";
import { type CSSProperties, useRef } from "react";
import { A11y, Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import Image from "next/image";
import { usePathname } from "next/navigation";
import type { SolutionPageContent } from "@/app/servicos/modernizacao-de-sistemas-legados/content/content";
import DifferencesLeft from "~/public/assets/backgrounds/bg-flow-left.png";
import DifferencesRight from "~/public/assets/backgrounds/bg-flow-right.png";
import type { ServicePageContent } from "../../../types/types";
import { useServiceCardsAnimation } from "./use-service-cards-animation";

type PhasesServicesSection = {
	service: ServicePageContent | SolutionPageContent;
};

type PhaseCardProps = {
	card: NonNullable<ServicePageContent["phases"]>["cards"][number];
	index: number;
	isSolutionsPage: boolean;
};

function PhaseCard({ card, index, isSolutionsPage }: PhaseCardProps) {
	return (
		<article
			data-service-card
			className={`flex h-full flex-col gap-6 p-8 2xl:p-12! rounded-2xl
			${!isSolutionsPage ? "bg-gray-1100" : "bg-[#171717]"}`}
		>
			<p className="font-bricolage text-primary-300 text-xl sm:text-2xl lg:text-4xl">
				0{index + 1}
			</p>
			<h3 className="min-h-14 text-lg font-bold md:text-xl">{card.title}</h3>
			<p className="text-sm leading-relaxed text-white/60 md:text-base">
				{card.description}
			</p>
		</article>
	);
}

export default function PhasesServicesSection({
	service,
}: PhasesServicesSection) {
	const phases = service.phases;
	const sectionRef = useRef<HTMLElement | null>(null);
	const pathname = usePathname();

	const isSolutionsPage = pathname.includes("modernizacao-de-sistemas-legados");

	useServiceCardsAnimation(sectionRef, {
		preset: "tilt",
	});

	if (!phases) return null;

	const phasesGridStyle = {
		"--phases-columns": phases.cards.length,
	} as CSSProperties;

	return (
		<section
			ref={sectionRef}
			className={`relative text-white border-t border-gray-300
			${isSolutionsPage ? "bg-gray-1100 border-b border-gray-300" : ""}`}
		>
			{!isSolutionsPage && (
				<>
					<Image
						src={DifferencesLeft}
						alt=""
						aria-hidden="true"
						className="hidden 2xl:block h-auto w-full max-w-[8%] 3xl:max-w-[9.7%] max-h-[70%] top-0 left-0 absolute"
					/>
					<Image
						src={DifferencesRight}
						alt=""
						aria-hidden="true"
						className="hidden 2xl:block h-auto w-full max-w-[8.5%] 3xl:max-w-[9.7%] max-h-[70%] top-0 right-0 absolute"
					/>
				</>
			)}
			<div className="md:container-padding">
				<div className="container relative md:border-x md:border-gray-300 px-6 lg:px-18 3xl:px-24! py-18 lg:py-26 lg:pb-250 xl:pb-180 3xl:pb-150!">
					<span className="hidden md:inline-flex absolute -top-1.75 -left-1.75 h-3 w-3 border border-gray-300 bg-gray-1000 z-40" />
					<span className="hidden md:inline-flex absolute -top-1.75 -right-1.75 h-3 w-3 border border-gray-300 bg-gray-1000 z-40" />

					<div
						className={`flex gap-6 md:gap-12 items-end justify-between max-md:flex-col
						${isSolutionsPage ? "text-center mx-auto" : ""}`}
					>
						<div
							className={` ${!isSolutionsPage ? "max-w-140" : "md:mx-auto"}`}
						>
							<p className="tag">{phases.tag}</p>
							<h2 className="title">{phases.title}</h2>
						</div>
						<p className="description max-w-86.5 space-y-8 md:text-right leading-[140%]">
							{phases.description}
						</p>
					</div>

					<div data-service-cards-animation-trigger className="mt-14 lg:hidden">
						<Swiper
							a11y={{ enabled: true }}
							className="overflow-visible!"
							modules={[Navigation, A11y]}
							navigation={{
								prevEl: ".phases-prev",
								nextEl: ".phases-next",
							}}
							slidesPerView="auto"
							spaceBetween={20}
						>
							{phases.cards.map((card) => (
								<SwiperSlide className="max-w-[95%]! h-auto!" key={card.title}>
									<PhaseCard
										card={card}
										index={phases.cards.indexOf(card)}
										isSolutionsPage={isSolutionsPage}
									/>
								</SwiperSlide>
							))}
						</Swiper>

						<div className="mt-5 flex items-center gap-5">
							<button
								aria-label="Card anterior"
								className="phases-prev cursor-pointer text-white transition-all duration-300 ease-in-out"
								type="button"
							>
								<ArrowLeft
									className="size-9 text-primary-300"
									strokeWidth={1.5}
								/>
							</button>
							<button
								aria-label="Próximo card"
								className="phases-next cursor-pointer text-white transition-all duration-300 ease-in-out"
								type="button"
							>
								<ArrowRight
									className="size-9 text-primary-300"
									strokeWidth={1.5}
								/>
							</button>
						</div>
					</div>
				</div>
			</div>

			<div
				data-service-cards-animation-trigger
				className="absolute top-50 lg:top-70 hidden w-full gap-2 px-7 lg:grid lg:grid-cols-[repeat(var(--phases-columns),minmax(0,1fr))]"
				style={phasesGridStyle}
			>
				{phases.cards.map((card) => (
					<PhaseCard
						key={card.title}
						card={card}
						index={phases.cards.indexOf(card)}
						isSolutionsPage={isSolutionsPage}
					/>
				))}
			</div>
		</section>
	);
}
